/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
import {
  createApi,
  fetchBaseQuery,
  type BaseQueryApi,
  type EndpointDefinitions,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
  type QueryReturnValue,
} from '@reduxjs/toolkit/query/react';
import axios from 'axios';

import type { AppConfig } from '../../config/config.base';
import { apiConfig } from '../../config/config.bundle';
import type { ApiResponse, AppFetchArgs } from '../../types/api-types';
import { handleQueryInBatches } from '../../utils/api-utils';
import { logout } from '../slices/auth-slice';
import type { AppRootState } from '../store';

const GUEST_TOKEN_TTL = 840000;
let tokenFetchedAt: number;

let guestTokenPromise: Promise<string> | null = null;

const getToken = async (state: AppRootState) => {
  if (!state.auth.user) {
    if (!guestTokenPromise || Date.now() - tokenFetchedAt > GUEST_TOKEN_TTL) {
      guestTokenPromise = axios
        .get<
          ApiResponse<string>
        >(`${apiConfig.account.baseUrl}${apiConfig.account.paths.accounts}?requestType=getToken`, { headers: { Authorization: 'unauthorized' } })
        .then(tokenResponse => tokenResponse.data.responseObject);
    }

    try {
      return await guestTokenPromise;
    } finally {
      tokenFetchedAt = Date.now();
    }
  } else {
    return `${state.auth.user.jwt}|${state.auth.user.sso}`;
  }
};

const handleResult = (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  api: BaseQueryApi
) => {
  if (
    result.error &&
    typeof result.error.status === 'number' &&
    [401, 403].includes(result.error.status)
  ) {
    api.dispatch(logout());
    location.replace('/login');
  }

  return result;
};

const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: async (args: string | AppFetchArgs, api, extraOptions) => {
    const state = api.getState() as AppRootState;
    // Get the base url based on the scope
    const baseUrl =
      apiConfig[(extraOptions as { service: keyof typeof apiConfig }).service]
        .baseUrl;

    const fetchArgs: typeof args =
      typeof args === 'string'
        ? `${baseUrl}${args}`
        : { ...args, url: `${baseUrl}${args.url}` };

    const token = await getToken(state);

    const rawFetchBaseQuery = fetchBaseQuery({
      prepareHeaders: headers => {
        headers.set('Authorization', token);
        return headers;
      },
      // credentials: 'include',
    });
    if (
      typeof fetchArgs !== 'string' &&
      fetchArgs.meta?.batch &&
      (fetchArgs.meta.batch.in === 'body'
        ? fetchArgs.body[fetchArgs.meta.batch.property].length >
          fetchArgs.meta.batch.size
        : fetchArgs.params?.[fetchArgs.meta.batch.property].split(
            fetchArgs.meta.batch.format
          ).length > fetchArgs.meta.batch.size)
    ) {
      const result = await handleQueryInBatches(
        rawFetchBaseQuery,
        fetchArgs as AppFetchArgs & {
          meta: {
            batch: NonNullable<NonNullable<AppFetchArgs['meta']>['batch']>;
          };
        },
        api,
        extraOptions
      );
      return handleResult(result, api);
    }

    const result = await rawFetchBaseQuery(fetchArgs, api, extraOptions);
    return handleResult(result, api);
  },
  tagTypes: [
    'Cart',
    'Favorites',
    'Lists',
    'SavedForLater',
    'Packages',
    'RecentSearches',
    'LocalUserDetails',
    'PropertyGroups',
    'LocalUsersOfAccount',
    'AddressesByPartyId',
    'LegacyOrderList',
    'OrderConsentData',
    'TruckDetails',
  ],
  endpoints: () => ({}),
});

export const getScopedApi = (
  service: keyof AppConfig['services']
): typeof baseApi => ({
  ...baseApi,
  injectEndpoints: <T extends EndpointDefinitions>(
    ...args: Parameters<typeof baseApi.injectEndpoints>
  ) => {
    const [config, ...rest] = args;

    return baseApi.injectEndpoints(
      {
        ...config,
        endpoints: builder => {
          const endpoints = config.endpoints(builder);

          Object.keys(endpoints).forEach(key => {
            const endpoint = (endpoints as any)[key];
            endpoint.extraOptions = {
              ...(endpoint.extraOptions || {}),
              service,
            };
          });

          return endpoints as T;
        },
      },
      ...rest
    );
  },
});

export default baseApi;
